import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Alert,
  Button,
  Tooltip // Import Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { AnimalGroup } from '../../../../types/animal';
import StatusChip from './StatusChip';

interface AnimalGroupGridProps {
  animalGroups: AnimalGroup[];
  selectedGroup: AnimalGroup | null;
  onSelectGroup: (group: AnimalGroup) => void;
  onGroupMenuClick: (event: React.MouseEvent<HTMLElement>, group: AnimalGroup) => void;
  onAddBulkRecord: (group: AnimalGroup) => void;
  onAddAnimal?: (group: AnimalGroup) => void;
  onBulkAddAnimals?: (group: AnimalGroup) => void;
  duplicateGroupId: string | null;
}

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
  borderRadius: theme.shape.borderRadius * 2,
}));

const AnimalGroupGrid: React.FC<AnimalGroupGridProps> = ({
  animalGroups,
  selectedGroup,
  onSelectGroup,
  onGroupMenuClick,
  onAddBulkRecord,
  onAddAnimal,
  onBulkAddAnimals,
  duplicateGroupId
}) => {
  return (
    <Grid container spacing={2}>
      {animalGroups.map((group) => {
        // Calculate financial stats for the group
        const groupValue = group.individuals?.reduce((sum, animal) => sum + (Number(animal.assetValue) || 0), 0) || 0;
        const groupRevenue = group.individuals?.reduce((sum, animal) => {
          const animalRevenue = (animal.yields || []).reduce((yieldSum, y) => 
            yieldSum + ((Number(y.dailyAvgYield) || 0) * (Number(y.marketPricePerUnit) || 0) * 30), 0);
          return sum + animalRevenue;
        }, 0) || 0;

        // Determine primary health chips (healthy vs issues)
        const healthEntries = Object.entries(group.healthySummary || {});
        const sortedHealth = healthEntries.sort((a, b) => b[1] - a[1]);

        return (
          <Grid item xs={12} sm={6} md={4} key={group.id}>
            <StyledCard 
              onClick={() => onSelectGroup(group)}
              sx={{ 
                cursor: 'pointer',
                bgcolor: selectedGroup?.id === group.id ? 'action.selected' : 'background.paper',
                ...(duplicateGroupId === group.id
                  ? {
                      animation: 'zoomGlow 1.5s',
                      border: '3px solid',
                      borderColor: 'warning.main',
                      zIndex: 10,
                    }
                  : {}),
              }}
            >
              <IconButton
                size="small"
                onClick={(e) => onGroupMenuClick(e, group)}
                sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}
              >
                <MoreVertIcon />
              </IconButton>
              <CardContent>
                <Typography variant="h6" gutterBottom>{group.breed} {group.type}</Typography>
                <Typography variant="h3" sx={{ fontWeight: 800 }}>{group.individuals?.length || 0}</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Total Animals
                </Typography>
                
                <Box sx={{ my: 2, p: 1.5, bgcolor: 'background.default', borderRadius: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography variant="caption" display="block" color="textSecondary">Herd Value</Typography>
                            <Typography variant="body2" fontWeight="bold" color="secondary.main">
                                Ksh {groupValue.toLocaleString()}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Tooltip 
                            title="Sum of (Avg Daily Yield * Market Price * 30 days) for all yield types of all animals in this group."
                            arrow
                            placement="top"
                          >
                            <Box>
                              <Typography variant="caption" display="block" color="textSecondary">Est. Monthly Rev</Typography>
                              <Typography variant="body2" fontWeight="bold" color="success.main">
                                  Ksh {Math.round(groupRevenue).toLocaleString()}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </Grid>
                    </Grid>
                </Box>
                
                {/* Last updated chip derived from individuals' lastHealthCheck */}
                {(() => {
                  const dates = (group.individuals || [])
                    .map(a => a.lastHealthCheck)
                    .filter(Boolean) as string[];
                  if (dates.length === 0) return null;
                  const latest = dates.sort().reverse()[0];
                  return (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Last update: {latest}
                    </Typography>
                  );
                })()}

                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: 1.2, 
                  mb: 2,
                  p: 1.2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.default'
                }}>
                  <Button 
                    variant="contained"
                    startIcon={<AddIcon />} 
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onAddAnimal && onAddAnimal(group); }}
                  >
                    Add Animal
                  </Button>
                  <Button 
                    variant="outlined"
                    startIcon={<GroupAddIcon />} 
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onBulkAddAnimals && onBulkAddAnimals(group); }}
                  >
                    Bulk Add Animals
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    startIcon={<MedicalServicesIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddBulkRecord(group);
                    }}
                  >
                    Group Health Record
                  </Button>
                </Box>

                <Box sx={{ mt: 1 }}>
                  <Grid container spacing={1}>
                    {sortedHealth.map(([status, count]) =>
                      count > 0 && (
                        <Grid key={status}>
                          <StatusChip
                            label={`${count} ${status.charAt(0).toUpperCase() + status.slice(1)}`}
                            size="small"
                            status={status}
                          />
                        </Grid>
                      )
                    )}
                  </Grid>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default AnimalGroupGrid;
